import { useMemo, type VoidFunctionComponent } from 'react';
import { useDispatch } from 'react-redux';

import { isCustomLicense, type LicenseGroup } from '../../../../../../../shared/licenses';
import { useDresser } from '../../../../../../contexts/DrawerContext';
import { useAddLicense } from '../../../../../../hooks/api/correctionsV2/useAddLicense';
import useLicenses from '../../../../../../hooks/api/licenses/useLicenses';
import useEditable from '../../../../../../hooks/useEditable';
import * as DependenciesV2Store from '../../../../../../redux/dependenciesV2';
import { selectOptionFactory } from '../../../../../../utils';
import Button from '../../../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../../../generic/Button/button-constants';
import LabeledSelect from '../../../../../generic/Forms/LabeledSelect';
import LabeledTextArea from '../../../../../generic/Forms/LabeledTextArea';
import LabeledTextInput from '../../../../../generic/Forms/LabeledTextInput';
import SupportLink from '../../../../../generic/Link/SupportLink';
import type { UpdateProjectCorrectionRequestLicense } from '../../../../../../../shared/types/licenses';

import styles from './DependencyLicenses.module.less';

type Props = {
  licenseGroup?: LicenseGroup;
  dependencyRevisionLocator: string;
  parentRevisionLocator?: string;
};

type NewLicenseData = UpdateProjectCorrectionRequestLicense;

const initialData: NewLicenseData = {
  licenseId: '',
  text: '',
  copyright: '',
};

export const AddLicense: VoidFunctionComponent<Props> = ({
  licenseGroup,
  dependencyRevisionLocator,
  parentRevisionLocator,
}) => {
  const { popDrawer } = useDresser();
  const dispatch = useDispatch();

  const { mutateAsync: addLicense, isPending } = useAddLicense(dependencyRevisionLocator);
  const { data: licenses, isLoading: isLoadingLicenses } = useLicenses(false, { pageSize: 5000 });

  const licenseOptions = useMemo(() => {
    return licenses?.map((license) =>
      selectOptionFactory(license.licenseId, `${license.name} (${license.licenseId})`),
    );
  }, [licenses]);

  const {
    data,
    onUpdate,
    onSave,
  } = useEditable<NewLicenseData>(initialData);

  const isValid = useMemo(() => {
    if (!data.licenseId) {
      return false;
    }

    if (isCustomLicense(data.licenseId) && !data.title?.trim()) {
      return false;
    }
    return true;
  }, [data.licenseId, data.title]);

  const onAddLicense = async () => {
    try {
      await addLicense({
        body: {
          data,
          parentRevisionLocator,
          licenseGroupId: licenseGroup?.licenseGroupId,
        },
        dependencyRevisionLocator,
      });
      dispatch(DependenciesV2Store.actions.preFetchLoading());
      dispatch(DependenciesV2Store.actions.fetchDependencies());
      onSave();
      popDrawer();
    } catch {
      // Error toast is handled by useToastedMutation
    }
  };

  const getSelectedLicenseOption = useMemo(() => {
    if (!data.licenseId) {
      return undefined;
    }
    return selectOptionFactory(
      data.licenseId || '',
      `${licenses?.find((license) => license.licenseId === data.licenseId)?.name} (${data.licenseId})`,
    );
  }, [data.licenseId, licenses]);

  return (
    <div className={styles.addLicenseContainer}>
      <div className={styles.header}>Add License</div>
      <div className={styles.input}>
        <LabeledSelect
          variant="legacy"
          label="License Name"
          id="license-name"
          value={getSelectedLicenseOption}
          onChange={(t) => {
            const selected = licenses?.find((l) => l.licenseId === t?.value);
            onUpdate({
              licenseId: t?.value || '',
              text: selected?.licenseText || '',
              title: undefined,
            });
          }}
          options={licenseOptions || []}
          isDisabled={isLoadingLicenses}
          placeholder="Select a license"
        />
      </div>
      {data.licenseId && isCustomLicense(data.licenseId) && (
        <div className={styles.input}>
          <LabeledTextInput
            labelVariant="legacy"
            label="Custom License Name"
            value={data.title}
            onChange={(title) => onUpdate({ title })}
            isRequired
            placeholder="Add a custom license name"
          />
        </div>
      )}
      <div className={styles.input}>
        <LabeledTextArea
          labelVariant="legacy"
          label="License Text"
          value={data.text}
          onChange={(text) => onUpdate({ text })}
          placeholder="Add the full text of the license here"
        />
      </div>
      <div className={styles.input}>
        <LabeledTextArea
          labelVariant="legacy"
          label="Copyrights"
          value={data.copyright}
          onChange={(copyright) => onUpdate({ copyright })}
          placeholder="Add copyright information here"
        />
      </div>
      <div className={styles.helpText}>
        Can&apos;t find the license you&apos;re looking for? <SupportLink>Contact support</SupportLink> to request it be
        added.
      </div>
      <hr />
      <div className={styles.footer}>
        <Button
          onClick={popDrawer}
          type={BUTTON_TYPE.SECONDARY}
          size={BUTTON_SIZE.MEDIUM}
        >
          Cancel
        </Button>
        <Button
          onClick={onAddLicense}
          type={BUTTON_TYPE.PRIMARY}
          size={BUTTON_SIZE.MEDIUM}
          isDisabled={isPending || !isValid}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};
